import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';

// Components
import { LocaleProvider } from './components/LocaleProvider';
import { Button } from './components/Button';

// Views
import { StartView } from './views/Start';
import { PollingView } from './views/Polling';
import { AlreadyMemberView } from './views/AlreadyMember';
import { FormView } from './views/Form';
import { DetailsView } from './views/Details';
import { CompleteView } from './views/Complete';
import { TooOldView } from './views/TooOld';
import { CheckoutView } from './views/Checkout';

// API
import { isMrmProduction, sendApplication, sendApplicationNoEId } from './api';

// Utilities
import { createMembership } from './utilities/createMembership';
import { DebugContext } from './utilities/debugContext';

// i18n
import { LanguageCode, DEFAULT_LOCALE, TranslationKey } from './translations';

// Context
import { useForm } from './context/form-context/hooks';
import {
  setDebug,
  setEnableMemberCodeField,
  setFormMode,
  setFormScene,
  setResetForm,
  setTypeOfMember,
  setUmbracoPageId,
} from './context/form-context/actions';

// typescript guard for locale
function isLocale(tested: string): tested is LanguageCode {
  return (tested as LanguageCode) !== undefined;
}

interface Props {
  language?: string;
  debug?: boolean;
  disabledEId: boolean;
  storageKey: string;
  enableMemberCodeField: boolean;
  umbracoPageId: number | undefined;
  antiForgeryToken?: string;
}
const App: React.FC<Props> = ({
  language,
  debug,
  disabledEId,
  storageKey,
  enableMemberCodeField,
  umbracoPageId,
  antiForgeryToken,
}) => {
  const [mrmProd, setMrmProd] = useState<boolean>(true);
  useEffect(() => {
    if (debug) {
      isMrmProduction().then((response) => {
        setMrmProd(response.data.isMrmProduction);
      });
    }
  }, []);

  useEffect(() => {
    if (antiForgeryToken) {
      window.__CSRF_TOKEN__ = antiForgeryToken;
    }
  }, [antiForgeryToken]);

  const { state, dispatch } = useForm();

  useEffect(() => {
    if (dispatch) {
      dispatch(setUmbracoPageId(umbracoPageId));
    }
  }, [umbracoPageId]);

  useEffect(() => {
    if (dispatch) {
      dispatch(setDebug(debug || false));
    }
  }, [debug]);

  useEffect(() => {
    if (dispatch) {
      dispatch(setFormMode(!disabledEId ? 'eId' : 'no-eId'));
    }
  }, [disabledEId]);

  useEffect(() => {
    if (dispatch) {
      dispatch(setEnableMemberCodeField(enableMemberCodeField));
    }
  }, [enableMemberCodeField]);

  // React query
  const [queryClient] = useState(() => new QueryClient());

  // Constrol debug mode
  const [debugEnabled, setDebugEnabled] = useState<boolean>(
    debug ? true : false
  );

  const [submissionErrorKey, setSubmissionErrorKey] =
    useState<TranslationKey | null>(null);

  // Loading indicators
  const [isLoadingSubmission, setIsLoadingSubmission] =
    useState<boolean>(false);

  // Whenever the activeScene changes run the following
  useEffect(() => {
    // Reset submission errors, if any
    setSubmissionErrorKey(null);

    // Reset window scroll position
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Track the change with Google Analytics
    // trackPageView(`/medlemsansokan/${state.activeScene.toLowerCase()}`);

    if (
      state.activeScene === 'TOO-OLD' ||
      state.activeScene === 'COMPLETION' ||
      (state.activeScene === 'ALREADY-A-MEMBER' &&
        state.union === 'Akademikernas a-kassa')
    ) {
      // Clear storage so the user will see the start view when refreshing
      setTimeout(() => {
        window.sessionStorage.removeItem(storageKey);
      }, 1);
    }
  }, [state.activeScene]);

  const handleOnReset = () => {
    if (dispatch) {
      window.sessionStorage.removeItem(storageKey);
      dispatch(setResetForm());
    }
  };

  const handleOnSubmitApplication = async (): Promise<void> => {
    if (state.paymentInformation) {
      setSubmissionErrorKey(null);
      setIsLoadingSubmission(true);
      const membership = createMembership({
        contactInformation: state.contactInfo,
        questions: state.answersToQuestions,
        union: state.union,
        consentToAuthorization: state.givenConsent || false,
        paymentInformation: state.paymentInformation,
        culture: language,
        umbracoPageId: state.umbracoPageId,
      });

      try {
        const response = await sendApplication(membership);
        if (dispatch) {
          // If the Messages array was empty that means that the user is now a member
          // Otherwise the submission is under review by AEA
          dispatch(
            setTypeOfMember(
              response.data.messages?.length === 0 ? 'automatic' : 'manually'
            )
          );
          dispatch(setFormScene('COMPLETION'));
        }
      } catch (err) {
        const axiosError = err as AxiosError;
        switch (axiosError.response?.status) {
          // This means that the user is not authorized to submit the application
          // Send user to login again
          case 403:
            handleOnReset();
            break;
          case 400:
            setSubmissionErrorKey(TranslationKey.submissionErrorBadRequest);
            break;
          default:
            setSubmissionErrorKey(TranslationKey.errorSubmissionError);
            break;
        }
      }
      setIsLoadingSubmission(false);
    }
  };

  const handleOnSubmitApplicationNoEId = async (): Promise<void> => {
    if (state.paymentInformation) {
      setSubmissionErrorKey(null);
      setIsLoadingSubmission(true);
      const membership = createMembership({
        contactInformation: state.contactInfo,
        questions: state.answersToQuestions,
        union: state.union,
        consentToAuthorization: state.givenConsent || false,
        paymentInformation: state.paymentInformation,
        culture: language,
        umbracoPageId: state.umbracoPageId,
      });
      try {
        await sendApplicationNoEId(membership);
        if (dispatch) {
          dispatch(
            // Always set the type of member to manually when no eId is used
            setTypeOfMember('manually')
          );
          dispatch(setFormScene('COMPLETION'));
        }
      } catch (err) {
        const axiosError = err as AxiosError;
        switch (axiosError.response?.status) {
          case 400:
            setSubmissionErrorKey(TranslationKey.submissionErrorBadRequest);
            break;
          default:
            setSubmissionErrorKey(TranslationKey.errorSubmissionError);
            break;
        }
      }
      setIsLoadingSubmission(false);
    }
  };

  const toggleDebugMode = () => {
    setDebugEnabled((prevState) => !prevState);
  };

  const locale = language && isLocale(language) ? language : DEFAULT_LOCALE;

  const getView = (): JSX.Element | null => {
    switch (state.activeScene) {
      case 'START':
        return <StartView />;
      case 'TOO-OLD':
        return <TooOldView />;
      case 'WAITING-FOR-SIGNATURE':
        return <PollingView />;
      case 'ALREADY-A-MEMBER':
        return <AlreadyMemberView />;
      case 'FORM':
        return <FormView />;
      case 'DETAILS':
        return <DetailsView />;
      case 'CHECKOUT':
        return (
          <CheckoutView
            isLoading={isLoadingSubmission}
            onSubmit={
              state.formMode === 'eId'
                ? handleOnSubmitApplication
                : handleOnSubmitApplicationNoEId
            }
            submissionErrorKey={submissionErrorKey}
          />
        );
      case 'COMPLETION':
        return <CompleteView />;
      default:
        return null;
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider languageCode={locale}>
        <DebugContext.Provider value={{ debugEnabled, toggleDebugMode }}>
          <div className="membership-app">
            {!mrmProd && (
              <span className="bg-warning font-bold text-sm border border-warning-low radius-xs padding-3xs position-absolute top-2xs right-xs">
                Mrm Test
              </span>
            )}
            {getView()}
            {state.debug && (
              <div className="padding-top-xs margin-top-lg border-top border-2 border-contrast-low flex gap-3xs flex-column justify-center">
                <p className="text-md text-uppercase color-green text-center font-primary font-bold">
                  Debug view
                </p>
                <Button
                  layout="secondary"
                  title={'Återställ allt'}
                  onClick={handleOnReset}
                />
              </div>
            )}
          </div>
        </DebugContext.Provider>
      </LocaleProvider>
    </QueryClientProvider>
  );
};

App.defaultProps = {
  debug: false,
};

export default App;
